import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import AdminFloorGrid from "../components/AdminFloorGrid";
import AdminChatModerationChrome from "../chat/AdminChatModerationChrome";
import ReportedMessagesIndex from "../chat/floor/ReportedMessagesIndex";
import ReportedMessageDetail from "../chat/floor/ReportedMessageDetail";
import {
  dismissAdminChatReport,
  fetchAdminReportedMessages,
} from "../chat/api/fetchAdminChatReports";
import { toggleUserBan } from "../../redux/slices/userManagementSlice";
import "../styles/admin-markets.css";
import "../styles/admin-markets-floor.css";
import "../chat/admin-chat-moderation.css";

const PAGE_SIZE = 20;

export default function ChatModerationPage() {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [banLoading, setBanLoading] = useState(false);
  const [dismissLoading, setDismissLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdminReportedMessages({
        page,
        limit: PAGE_SIZE,
        channel: "lounge",
      });
      setItems(data?.items || []);
      setSummary(data?.summary || null);
      setPagination((prev) => ({
        ...prev,
        ...(data?.pagination || {}),
        limit: data?.pagination?.limit || PAGE_SIZE,
      }));
    } catch (err) {
      setItems([]);
      setError(err.response?.data?.error || err.message || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!items.length) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !items.some((item) => item.id === selectedId)) {
      setSelectedId(items[0].id);
    }
  }, [items, selectedId]);

  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedId) || null,
    [items, selectedId]
  );

  const handleBan = async (userId, banReason) => {
    if (!userId) return;
    setBanLoading(true);
    try {
      const result = await dispatch(toggleUserBan({ userId, banReason })).unwrap();
      if (result?.success) {
        toast.success(result.message || "Ban status updated");
        setItems((prev) =>
          prev.map((row) =>
            row.author?._id === userId
              ? {
                  ...row,
                  author: {
                    ...row.author,
                    isBanned: result.user.isBanned,
                    banReason: result.user.banReason,
                  },
                }
              : row
          )
        );
      }
    } catch (err) {
      toast.error(err || "Failed to update ban status");
    } finally {
      setBanLoading(false);
    }
  };

  const handleDismiss = async (item) => {
    if (!item?.messageId || !item?.channel) return;
    setDismissLoading(true);
    try {
      await dismissAdminChatReport(item.channel, item.messageId);
      toast.success("Report dismissed");
      const nextItems = items.filter((row) => row.id !== item.id);
      setItems(nextItems);
      setSummary((prev) => {
        if (!prev) return prev;
        return {
          loungeCount: Math.max(0, prev.loungeCount - 1),
          fixtureCount: 0,
          total: Math.max(0, prev.total - 1),
        };
      });
      setSelectedId(nextItems[0]?.id || null);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Failed to dismiss report");
    } finally {
      setDismissLoading(false);
    }
  };

  return (
    <div className="am-page ach-page">
      <div className="am-aurora" aria-hidden>
        <span className="am-aurora-a ach-aurora" />
      </div>

      <AdminChatModerationChrome
        summary={summary}
        loading={loading}
        onRefresh={load}
      />

      {error ? (
        <div className="ach-error" role="alert">{error}</div>
      ) : null}

      <div className="am-board am-board-floor">
        <AdminFloorGrid
          sectionKey="chat-moderation"
          index={
            <ReportedMessagesIndex
              items={items}
              selectedId={selectedId}
              onSelect={(item) => setSelectedId(item.id)}
              loading={loading}
              pagination={{
                page: pagination.page,
                totalPages: pagination.totalPages,
                total: summary?.total ?? pagination.total,
                limit: PAGE_SIZE,
              }}
              onPageChange={setPage}
            />
          }
          detail={
            <ReportedMessageDetail
              item={selectedItem}
              banLoading={banLoading}
              dismissLoading={dismissLoading}
              onBan={handleBan}
              onDismiss={handleDismiss}
            />
          }
        />
      </div>
    </div>
  );
}
